import { motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { Instagram } from "lucide-react";
import Layout from "../common/Layout";
import { usePageMeta } from "../common/usePageMeta";
import { track } from "../features/analytics/tracker";

const ROLE_OPTIONS = ["Chef / kitchen", "Florist", "Retail / deli", "Other"];

export default function ContactPage() {
  usePageMeta({ title: 'Contact | TearDrop Hydro', description: 'Get in touch with TearDrop Hydroponics in Hockley - questions, orders, delivery areas or wholesale enquiries.', path: '/contact' });
  const [status, setStatus] = useState(""); // "success" | "error" | ""
  const [loading, setLoading] = useState(false);
  const [msgCount, setMsgCount] = useState(0);
  const [apiErr, setApiErr] = useState("");
  const formRef = useRef(null);

  const startedRef = useRef(false);
  const focusedRef = useRef(new Set());

  // We allow the click if it's not loading; 
  // validation is handled inside handleSubmit for better UX
  const isButtonDisabled = loading;

  // page view
  useEffect(() => {
    track("contact_view");
  }, []);

  function onFormStart() {
    if (startedRef.current) return;
    startedRef.current = true;
    track("contact_form_start");
  }

  function onFieldFocus(name) {
    if (focusedRef.current.has(name)) return;
    focusedRef.current.add(name);
    track("contact_form_field_focus", { field: name });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus("");
    setApiErr("");

    const form = formRef.current;
    if (!form) return;

    // Honeypot: Using a less common name to avoid browser autofill
    const hp = form.querySelector('input[name="b_username_verification"]')?.value?.trim();
    if (hp) {
      track("contact_form_bot_detected");
      setStatus("success");
      form.reset();
      setMsgCount(0);
      return;
    }

    const fd = new FormData(form);
    const body = Object.fromEntries(fd.entries());

    // Validation
    if (!body.name || !body.email || !body.message || body.message.length < 10) {
      track("contact_form_blocked_short_message", {
        msg_len: body.message?.length || 0
      });
      setStatus("error");
      setApiErr("Please complete all fields (message must be at least 10 characters).");
      return;
    }

    track("contact_form_submit_attempt", {
      role: body.role,
      preferred: body.preferred,
      has_postcode: !!body.postcode,
      msg_len: body.message.length
    });

    setLoading(true);
    try {
      const res = await fetch("https://teardrophydro.co.uk/garageFarm/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        track("contact_form_submit_success", {
          role: body.role,
          preferred: body.preferred,
          has_postcode: !!body.postcode,
          msg_len: body.message.length
        });

        setStatus("success");
        form.reset();
        setMsgCount(0);
      } else {
        const errorData = await res.text();
        console.error("Server Error:", errorData);
        throw new Error("Failed");
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      track("contact_form_submit_error");
      setStatus("error");
      setApiErr("Couldn’t send via the form. You can email me directly at contact@teardrophydro.co.uk.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout>
      {/* HERO */}
      <section style={{ position: "relative", overflow: "hidden", minHeight: "clamp(360px, 50svh, 580px)", display: "flex", alignItems: "center" }}>
        <img
          src="/images/products/genoBasil/IMG_4479.JPEG"
          alt=""
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 30%" }}
        />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(10,28,20,.82) 0%, rgba(10,28,20,.52) 60%, transparent 100%)" }} />
        <div className="container" style={{ position: "relative", zIndex: 1, padding: "var(--s-10) var(--s-5)" }}>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="eyebrow" style={{ color: "var(--accent)", marginBottom: "var(--s-3)" }}>Get in touch</p>
            <h1 className="h1" style={{ color: "#fff", maxWidth: 560, marginTop: 0, marginBottom: "var(--s-4)", fontSize: "clamp(28px,5vw,44px)", lineHeight: 1.1 }}>
              Let’s talk about your ideas
            </h1>
            <p style={{ color: "rgba(255,255,255,.8)", fontSize: "clamp(15px,1.8vw,18px)", maxWidth: 480, marginBottom: "var(--s-8)", lineHeight: 1.6 }}>
              Whether you’re a chef, florist, or just curious about the project, you can reach me directly below.
            </p>
            <div style={{ display: "flex", gap: "var(--s-4)", flexWrap: "wrap" }}>
              <a
                href="tel:+447541264513"
                className="btn btn-primary"
                onClick={() => track("contact_hero_cta_click", { cta: "phone" })}
              >
                Call / WhatsApp
              </a>
              <a
                href="mailto:contact@teardrophydro.co.uk"
                className="btn btn-ghost"
                style={{ color: "#fff", borderColor: "rgba(255,255,255,.4)" }}
                onClick={() => track("contact_hero_cta_click", { cta: "email_direct" })}
              >
                Email directly
              </a>
              <a
                href="#contact-form"
                className="btn btn-ghost"
                style={{ color: "#fff", borderColor: "rgba(255,255,255,.4)" }}
                onClick={() => track("contact_hero_cta_click", { cta: "use_form" })}
              >
                Use form
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <motion.main
        className="container section contact"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.45 }}
        style={{ padding: "0px" }}
      >
        {/* Grid */}
        <div className="contact-grid" style={{ paddingLeft: 20, paddingRight: 20 }}>
          {/* Details */}
          <motion.section
            className="contact-details"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <div className="detail-block">
              <h3 className="h4">Phone / WhatsApp</h3>
              <p><a href="tel:+447541264513" onClick={() => track("contact_detail_click", { cta: "phone" })}>+44 7541 264513</a></p>
            </div>
            <div className="detail-block">
              <h3 className="h4">Email</h3>
              <p><a href="mailto:contact@teardrophydro.co.uk">contact@teardrophydro.co.uk</a></p>
            </div>
            <div className="detail-block">
              <h3 className="h4">Location</h3>
              <p>Hockley, Essex, UK</p>
            </div>
            <div className="detail-block">
              <h3 className="h4">Response time</h3>
              <p className="muted">Phone / WhatsApp: same day. Email / form: usually within 24 hours.</p>
            </div>
            <div className="detail-block">
              <h3 className="h4">Instagram</h3>
              <p>
                <a
                  href="https://www.instagram.com/teardrop_hydro/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                  onClick={() => track("contact_detail_click", { cta: "instagram" })}
                >
                  <Instagram size={16} />
                  @teardrop_hydro
                </a>
              </p>
            </div>
          </motion.section>

          {/* Form */}
          <motion.section
            id="contact-form"
            className="contact-form-section"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="h3">Send a message</h2>

            <form
              ref={formRef}
              className="contact-form"
              onSubmit={handleSubmit}
              noValidate
              onFocus={onFormStart}
            >
              {/* Updated Honeypot */}
              <input 
                type="text" 
                name="b_username_verification" 
                style={{ display: 'none' }} 
                tabIndex={-1} 
                autoComplete="off" 
              />

              <label htmlFor="role">I’m a…</label>
              <select
                id="role"
                name="role"
                defaultValue={ROLE_OPTIONS[0]}
                onFocus={() => onFieldFocus("role")}
              >
                {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>

              <label htmlFor="name">Name</label>
              <input
                id="name"
                name="name"
                required
                minLength={2}
                onFocus={() => onFieldFocus("name")}
              />

              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                onFocus={() => onFieldFocus("email")}
              />

              <label htmlFor="postcode">Postcode (optional)</label>
              <input
                id="postcode"
                name="postcode"
                onFocus={() => onFieldFocus("postcode")}
              />

              <label htmlFor="pref">Preferred contact</label>
              <select
                id="pref"
                name="preferred"
                defaultValue="Email"
                onFocus={() => onFieldFocus("preferred")}
              >
                <option>Email</option>
                <option>Phone</option>
                <option>Either</option>
              </select>

              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                rows={5}
                required
                minLength={10}
                maxLength={1500}
                onFocus={() => onFieldFocus("message")}
                onInput={(e) => setMsgCount(e.currentTarget.value.length)}
              />
              <div className="muted small-count">{msgCount}/1500</div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={isButtonDisabled}
                aria-busy={loading}
              >
                {loading ? "Sending…" : "Send Message"}
              </button>

              <div aria-live="polite" className="form-live">
                {status === "success" && (
                  <p className="form-msg success">Thanks! I’ll get back to you soon.</p>
                )}
                {status === "error" && (
                  <p className="form-msg error">
                    {apiErr}{" "}
                    <a className="footer-link" href="mailto:contact@teardrophydro.co.uk">Open email</a>
                  </p>
                )}
              </div>
            </form>
          </motion.section>
        </div>
      </motion.main>
    </Layout>
  );
}